import { useState } from "react";
import InlineSvg from "../lib/InlineSvg.tsx";
import { composeSlide, createContent } from "../lib/resources.ts";
import { nameFor, pool, splitAnnouncements } from "../lib/bulk.ts";
import type { Orientation } from "../lib/types.ts";

// Bulk compose — paste a bulletin's worth of announcements and get a slide for
// each in one pass.
//
// The one-at-a-time composer is the right tool for crafting a single slide, and
// the wrong one for a volunteer holding a list of eight. This deliberately does
// NOT offer revision: the point is to get a set drafted and reviewed together,
// then reopen any individual dud with "Revise with AI" from the Content list.

/** Each item is a separate API call and each counts against AI_DAILY_CAP, which
 *  defaults to 25/day. Ten keeps one paste well clear of the cap. */
const MAX_ITEMS = 10;

/** Requests run in parallel, but not all at once — a dozen simultaneous calls
 *  invites rate limiting, and the results arrive faster than anyone can read
 *  them anyway. */
const CONCURRENCY = 3;

type Status = "queued" | "running" | "done" | "failed";

interface Draft {
  text: string;
  name: string;
  status: Status;
  svg: string | null;
  error: string | null;
  /** Unticked drafts are skipped when saving. */
  keep: boolean;
}

export default function BulkCompose({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [raw, setRaw] = useState("");
  const [orientation, setOrientation] = useState<Orientation>("landscape");
  const [drafts, setDrafts] = useState<Draft[] | null>(null);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = splitAnnouncements(raw);
  const tooMany = parsed.length > MAX_ITEMS;

  async function generate() {
    if (!parsed.length || tooMany) return;
    const initial: Draft[] = parsed.map((text, i) => ({
      text,
      name: nameFor(text, i),
      status: "queued",
      svg: null,
      error: null,
      keep: true,
    }));
    setDrafts(initial);
    setRunning(true);
    setError(null);

    const patch = (i: number, p: Partial<Draft>) =>
      setDrafts((cur) =>
        cur ? cur.map((d, j) => (j === i ? { ...d, ...p } : d)) : cur,
      );

    await pool(initial, CONCURRENCY, async (draft, i) => {
      patch(i, { status: "running" });
      try {
        const { svg } = await composeSlide(
          [{ role: "user", content: draft.text }],
          orientation,
        );
        patch(i, { status: "done", svg });
      } catch (e) {
        // One failure must not take the batch down — the others are still
        // useful, and the daily cap may simply have been reached partway.
        patch(i, {
          status: "failed",
          error: e instanceof Error ? e.message : "Generation failed",
          keep: false,
        });
      }
    });

    setRunning(false);
  }

  async function saveKept() {
    if (!drafts) return;
    const keepers = drafts.filter((d) => d.keep && d.svg);
    if (!keepers.length) return;
    setSaving(true);
    setError(null);
    try {
      // Sequential: these are small writes, and a partial failure is far easier
      // to explain when the order is predictable.
      for (const d of keepers) {
        await createContent({
          name: d.name.trim() || "AI slide",
          type: "svg",
          data: { svg: d.svg as string },
        });
      }
      onSaved();
    } catch (e) {
      setError(
        e instanceof Error
          ? `${e.message} — some slides may already have been added.`
          : "Save failed",
      );
      setSaving(false);
    }
  }

  const done = drafts?.filter((d) => d.status === "done").length ?? 0;
  const failed = drafts?.filter((d) => d.status === "failed").length ?? 0;
  const keeping = drafts?.filter((d) => d.keep && d.svg).length ?? 0;

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="composer-head">
          <h2>Make several slides at once</h2>
          <div className="seg">
            <button
              className={"seg-btn" + (orientation === "landscape" ? " seg-btn--on" : "")}
              onClick={() => setOrientation("landscape")}
              disabled={running || !!drafts}
            >
              Landscape
            </button>
            <button
              className={"seg-btn" + (orientation === "portrait" ? " seg-btn--on" : "")}
              onClick={() => setOrientation("portrait")}
              disabled={running || !!drafts}
            >
              Portrait
            </button>
          </div>
        </div>

        {!drafts && (
          <>
            <p className="muted">
              Paste your announcements — one per line, or separated by a blank
              line if any of them run long. Include the real details (day, date,
              time, place) for each. You'll get one slide per announcement and
              can throw away the ones you don't want.
            </p>
            <textarea
              className="bulk-input"
              rows={9}
              placeholder={
                "Youth night, Wednesday Jan 14 at 6:30pm in the Fellowship Hall — pizza provided\n" +
                "Ladies' Bible study, Tuesdays at 10am in the Fireside Room\n" +
                "Harvest Festival, Saturday October 18 at 5:30pm — free for all ages"
              }
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
            />
            <p className={"muted" + (tooMany ? " bulk-over" : "")}>
              {parsed.length === 0
                ? "Nothing to make yet."
                : `${parsed.length} announcement${parsed.length === 1 ? "" : "s"}`}
              {tooMany && ` — that's over the limit of ${MAX_ITEMS} at a time. Split it into two batches.`}
            </p>
          </>
        )}

        {drafts && (
          <>
            <p className="muted">
              {running
                ? `Generating… ${done + failed} of ${drafts.length} finished.`
                : `Done — ${done} made${failed ? `, ${failed} failed` : ""}. Untick anything you don't want, then add the rest.`}
            </p>
            <div className="bulk-grid">
              {drafts.map((d, i) => (
                <div className="bulk-item card" key={i}>
                  <div className="bulk-item-preview">
                    {d.svg ? (
                      <InlineSvg className="bulk-item-svg" svg={d.svg} />
                    ) : (
                      <span className="muted">
                        {d.status === "failed" ? "Failed" : "…"}
                      </span>
                    )}
                  </div>
                  <label className="bulk-item-keep">
                    <input
                      type="checkbox"
                      checked={d.keep}
                      disabled={!d.svg || saving}
                      onChange={(e) =>
                        setDrafts((cur) =>
                          cur
                            ? cur.map((x, j) =>
                                j === i ? { ...x, keep: e.target.checked } : x,
                              )
                            : cur,
                        )
                      }
                    />
                    <input
                      className="inline-input bulk-item-name"
                      value={d.name}
                      disabled={!d.svg || saving}
                      aria-label={`Name for slide ${i + 1}`}
                      onChange={(e) =>
                        setDrafts((cur) =>
                          cur
                            ? cur.map((x, j) =>
                                j === i ? { ...x, name: e.target.value } : x,
                              )
                            : cur,
                        )
                      }
                    />
                  </label>
                  {d.error && <div className="bulk-item-error">{d.error}</div>}
                </div>
              ))}
            </div>
          </>
        )}

        {error && <div className="banner banner--error">{error}</div>}

        <div className="modal-actions">
          <button className="btn btn--ghost" onClick={onClose} disabled={running || saving}>
            {drafts && !running ? "Discard" : "Cancel"}
          </button>
          {!drafts ? (
            <button
              className="btn"
              onClick={generate}
              disabled={!parsed.length || tooMany}
            >
              Make {parsed.length || ""} slide{parsed.length === 1 ? "" : "s"}
            </button>
          ) : (
            <button
              className="btn"
              onClick={saveKept}
              disabled={running || saving || keeping === 0}
            >
              {saving
                ? "Adding…"
                : `Add ${keeping} slide${keeping === 1 ? "" : "s"} to library`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
